'use client';

import { stegaClean } from '@sanity/client/stega';
import { motion } from 'framer-motion';
import { PortableText } from '@portabletext/react';

import { GroqImage } from '@/helpers/Image';
import { PageCareerDetailSchema } from '@/sanity/pages/PageCareerDetailTemplate';
import { richTextComponent } from '@/sanity/sanityPortableText';
import { CTAButton } from '@/shadcn/ui/button';

import CountryFlags from './CountryFlags';

const ease = [0.25, 0.1, 0.25, 1] as const;

function CheckIcon() {
  return (
    <div className="flex h-[4rem] w-[4rem] shrink-0 items-center justify-center bg-[#003828]">
      <svg viewBox="0 0 12 9" fill="none" className="h-[1.125rem] w-[1.5rem] shrink-0">
        <path
          d="M1 4.5L4.5 8L11 1"
          stroke="white"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
}

export default function CareerHeroSection({ data }: { data: PageCareerDetailSchema }) {
  const tags = (stegaClean(data.heroTags) ?? []).filter(Boolean);
  const checklist = data.heroChecklist?.filter(Boolean) ?? [];

  return (
    <section className="relative flex w-full flex-col items-center justify-center overflow-x-clip bg-white max-lg:pt-[8rem] max-lg:pb-[4rem] lg:pt-[10rem] lg:pb-[10rem]">
      {/* Background */}
      {data.heroBackgroundImage && (
        <motion.div
          initial={{ opacity: 0, filter: 'blur(8px)' }}
          animate={{ opacity: 1, filter: 'blur(0px)' }}
          transition={{ duration: 1.2, ease }}
          className="pointer-events-none absolute inset-0 h-full w-full [mask-image:linear-gradient(to_bottom,#000_70%,transparent)]"
          aria-hidden="true"
        >
          <GroqImage
            src={data.heroBackgroundImage}
            alt=""
            quality={100}
            className="!h-full !w-full !object-cover max-lg:!hidden"
          />
        </motion.div>
      )}

      <div className="relative z-10 mx-auto flex w-[min(140rem,calc(100%-4rem))] flex-col items-center max-lg:gap-[2.5rem] lg:gap-[4.25rem]">
        <div className="flex w-full max-w-[114.375rem] flex-col items-center max-lg:gap-[2.5rem] lg:gap-[4rem]">
          <div className="flex w-full flex-col items-center gap-[1.25rem]">
            {(data.heroIsInternational || (data.heroCountries?.length ?? 0) > 0) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.1 }}
              >
                <CountryFlags
                  countries={data.heroCountries}
                  isInternational={data.heroIsInternational}
                />
              </motion.div>
            )}

            {tags.length > 0 && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.2 }}
                className="mb-0 text-center font-blacker text-[2rem] italic leading-[1.5] text-black"
              >
                {tags.join(' · ')}
              </motion.p>
            )}

            {data.heroTitle && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.35 }}
                className="richtext w-full max-sm:max-w-[49rem] sm:max-w-[102rem] lg:mt-[0.75rem] prose-headings:mb-0 prose-headings:font-medium max-lg:prose-headings:text-[5rem] max-lg:prose-headings:leading-[1.12] max-lg:prose-headings:tracking-[-0.15rem] lg:prose-headings:text-[7rem] lg:prose-headings:leading-[1.12] lg:prose-headings:tracking-[-0.21rem] [&_h1]:[text-wrap:balance] [&_h2]:[text-wrap:balance]"
              >
                {/* Desktop title */}
                <div className="hidden w-full lg:block">
                  <PortableText value={data.heroTitle} components={richTextComponent} />
                </div>
                {/* Mobile title — falls back to the desktop title */}
                <div className="w-full lg:hidden">
                  <PortableText
                    value={data.heroTitleMobile ?? data.heroTitle}
                    components={richTextComponent}
                  />
                </div>
              </motion.div>
            )}

            {data.heroSubtitle && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, ease, delay: 0.45 }}
                className="richtext w-full max-sm:max-w-[46.5rem] sm:max-w-[min(86.5rem,88%)] lg:mt-[0.75rem] prose-p:mb-0 prose-p:[text-wrap:balance] prose-p:text-[2.25rem] prose-p:font-medium prose-p:leading-[1.5]"
              >
                <PortableText value={data.heroSubtitle} components={richTextComponent} />
              </motion.div>
            )}
          </div>

          {data.heroCTA?.cta && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease, delay: 0.55 }}
              className="w-full [&_.button]:whitespace-nowrap lg:w-fit lg:min-w-[29.75rem] lg:[&_.button]:min-h-[5.75rem] lg:[&_.button]:w-full"
            >
              <CTAButton data={data.heroCTA} />
            </motion.div>
          )}
        </div>

        {checklist.length > 0 && (
          <div className="relative w-full lg:w-auto">
            <div
              className="pointer-events-none absolute -inset-x-[2rem] -top-[3rem] bottom-[-5rem] z-0 lg:hidden"
              style={{
                backgroundImage: 'url(/careers/hero-dots.png)',
                backgroundSize: '102.5rem auto',
                backgroundRepeat: 'repeat',
                opacity: 0.18,
                maskImage: 'linear-gradient(to bottom, black 78%, transparent 100%)',
                WebkitMaskImage: 'linear-gradient(to bottom, black 78%, transparent 100%)',
              }}
              aria-hidden="true"
            />
            <motion.ul
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease, delay: 0.65 }}
              className="relative z-[1] mb-0 flex w-full list-none flex-col items-stretch justify-center gap-x-[1rem] max-lg:gap-y-[1rem] p-0 lg:w-auto lg:flex-row lg:flex-wrap lg:items-center lg:gap-y-[1.5rem]"
          >
            {checklist.map((item, index) => (
              <li key={`${item}-${index}`} className="flex w-full items-center gap-x-[1rem] lg:w-auto">
                <div className="flex h-[6.25rem] w-full items-center gap-[1.625rem] border border-[rgba(0,0,0,0.1)] bg-white p-[1.75rem] lg:w-auto lg:border-0">
                  <CheckIcon />
                  <span className="mb-0 whitespace-nowrap text-center font-geist-mono font-semibold uppercase max-lg:text-[2rem] lg:text-[1.625rem] leading-[1.33] text-black">
                    {item}
                  </span>
                </div>
                {index < checklist.length - 1 && (
                  <span aria-hidden="true" className="hidden h-[4.5625rem] w-px bg-black/20 lg:block" />
                )}
              </li>
            ))}
            </motion.ul>
          </div>
        )}
      </div>
    </section>
  );
}
